import { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { aiServerArchitectApi, permissionsManagerApi, pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Bot, Loader2, Send, RefreshCcw, PlayCircle, Wrench, Shield, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import { PermissionTreeView, type PermissionTreeChannel } from '@/components/permissions/PermissionTreeView';
import { PermissionDetailPanel, type PermissionTriState } from '@/components/permissions/PermissionDetailPanel';

type ChatMessage = {
  role: 'user' | 'assistant';
  text: string;
};

export function AIServerArchitectPlugin({ serverId }: { serverId: string }) {
  const { t } = useI18n();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [goals, setGoals] = useState('');
  const [allowDestructive, setAllowDestructive] = useState(false);
  const [blueprint, setBlueprint] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [lastApplyResult, setLastApplyResult] = useState<any>(null);
  const [liveEvents, setLiveEvents] = useState<Array<{ type: string; message: string }>>([]);
  const [streamingApply, setStreamingApply] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<PermissionTreeChannel | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [permissionDraft, setPermissionDraft] = useState<Record<string, PermissionTriState>>({});
  const [troubleshootInput, setTroubleshootInput] = useState('');
  const [pendingFix, setPendingFix] = useState<any>(null);
  const [troubleshootContext, setTroubleshootContext] = useState<any>(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [troubleshooting, setTroubleshooting] = useState(false);
  const [applyingFix, setApplyingFix] = useState(false);
  const [movingChannelId, setMovingChannelId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: plugins = [] } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const architectPlugin = (plugins as any[]).find((plugin) => plugin.pluginId === 'ai-server-architect');
  const isEnabled = architectPlugin?.enabled ?? false;

  const {
    data: structurePayload,
    isFetching: loadingStructure,
    refetch: refetchStructure,
  } = useQuery({
    queryKey: ['servers', serverId, 'ai-server-architect', 'structure'],
    queryFn: () => aiServerArchitectApi.getStructure(serverId),
    refetchInterval: 4000,
  });

  const structure = (structurePayload as any)?.structure;

  const { data: permissionTree, refetch: refetchPermissionTree } = useQuery({
    queryKey: ['servers', serverId, 'permissions', 'tree'],
    queryFn: () => permissionsManagerApi.getTree(serverId),
    enabled: isEnabled,
  });

  const treeRoles = useMemo(() => {
    const roles = (permissionTree as any)?.roles || [];
    return roles.map((role: any) => ({ id: role.id, name: role.name }));
  }, [permissionTree]);

  const channelPermissions = useMemo(() => {
    if (!selectedChannel) return [];
    return selectedChannel.type === 'voice' || selectedChannel.type === 'stage'
      ? ['ViewChannel', 'Connect', 'Speak', 'Stream', 'UseVAD']
      : ['ViewChannel', 'SendMessages', 'ReadMessageHistory', 'ManageMessages', 'AttachFiles', 'EmbedLinks'];
  }, [selectedChannel]);

  useEffect(() => {
    if (!selectedChannel || !selectedRoleId) {
      setPermissionDraft({});
      return;
    }

    const overwrite = selectedChannel.permissionOverwrites?.find((entry) => entry.roleId === selectedRoleId);
    const draft: Record<string, PermissionTriState> = {};
    for (const perm of channelPermissions) {
      if (!overwrite) draft[perm] = 'INHERIT';
      else if (overwrite.deny.includes(perm)) draft[perm] = 'DENY';
      else if (overwrite.allow.includes(perm)) draft[perm] = 'ALLOW';
      else draft[perm] = 'INHERIT';
    }
    setPermissionDraft(draft);
  }, [selectedChannel, selectedRoleId, channelPermissions]);

  useEffect(() => {
    if (!selectedRoleId && treeRoles.length > 0) {
      setSelectedRoleId(treeRoles[0].id);
    }
  }, [treeRoles, selectedRoleId]);

  const { mutate: togglePlugin, isPending: togglingPlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled
        ? pluginApi.enablePlugin(serverId, 'ai-server-architect')
        : pluginApi.disablePlugin(serverId, 'ai-server-architect'),
    {
      invalidateQueries: [['servers', serverId, 'plugins']],
      onSuccess: () => toast.success(t('architect.updated')),
    }
  );

  const { mutate: chatWithAI, isPending: chatting } = useApiMutation(
    (payload: { message: string }) =>
      aiServerArchitectApi.chat(serverId, {
        message: payload.message,
        currentBlueprint: blueprint || (structurePayload as any)?.blueprint,
        goals,
        allowDestructive,
      }),
    {
      onSuccess: (response: any) => {
        setMessages((prev) => [...prev, { role: 'assistant', text: String(response.reply || t('architect.operationCompleted')) }]);
        setBlueprint(response.blueprint);
        setPlan(response.plan);
      },
      onError: (error: any) => {
        toast.error(error?.message || t('architect.chatError'));
      },
    }
  );

  const handleApplyStream = async (dryRun: boolean) => {
    if (!isEnabled) {
      toast.error(t('architect.enableFirst'));
      return;
    }
    if (!blueprint && !(structurePayload as any)?.blueprint) {
      toast.error(t('architect.noBlueprint'));
      return;
    }

    setStreamingApply(true);
    setLiveEvents([]);

    try {
      await aiServerArchitectApi.applyStream(
        serverId,
        {
          blueprint: blueprint || (structurePayload as any)?.blueprint,
          dryRun,
          allowDestructive,
        },
        (event) => {
          setLiveEvents((prev) => [...prev, { type: String(event.type || 'info'), message: String(event.message || '') }]);

          if (event.type === 'result' && event.data) {
            setLastApplyResult(event.data.result);
            setPlan(event.data.plan);
            toast.success(event.data?.result?.dryRun ? t('architect.simulationCompleted') : t('architect.appliedToDiscord'));
          }

          if (event.type === 'error') {
            toast.error(event.message || t('architect.streamError'));
          }
        }
      );
    } catch (error: any) {
      toast.error(error?.message || t('architect.applyStreamingError'));
    } finally {
      setStreamingApply(false);
      refetchStructure();
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    if (!isEnabled) {
      toast.error(t('architect.enableFirst'));
      return;
    }

    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    chatWithAI({ message: text });
  };

  const handleSelectChannel = (channel: PermissionTreeChannel, categoryName: string) => {
    setSelectedChannel(channel);
    setSelectedCategoryName(categoryName);
    setPendingFix(null);
  };

  const handleSavePermissions = async () => {
    if (!selectedChannel || !selectedRoleId) return;
    setSavingPermissions(true);
    try {
      const response: any = await permissionsManagerApi.updateChannelOverwrites(serverId, selectedChannel.id, {
        roleId: selectedRoleId,
        changes: permissionDraft,
      });
      queryClient.setQueryData(['servers', serverId, 'permissions', 'tree'], response.tree);
      const updated = (response.tree as any)?.categories
        ?.flatMap((cat: any) => cat.channels.map((ch: any) => ({ ...ch, categoryName: cat.name })))
        ?.find((ch: any) => ch.id === selectedChannel.id);
      if (updated) {
        setSelectedChannel(updated);
        setSelectedCategoryName(updated.categoryName || selectedCategoryName);
      }
      toast.success('Permessi aggiornati su Discord');
      refetchStructure();
    } catch (error: any) {
      toast.error(error?.message || 'Errore aggiornamento permessi');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleTroubleshoot = async () => {
    const text = troubleshootInput.trim();
    if (!text || !isEnabled) return;

    setTroubleshooting(true);
    setPendingFix(null);
    try {
      const response: any = await permissionsManagerApi.troubleshoot(serverId, { message: text });
      setTroubleshootContext(response.context);
      setPendingFix(response.fix);
      toast.success('Analisi IA completata');
    } catch (error: any) {
      toast.error(error?.message || 'Errore troubleshooting IA');
    } finally {
      setTroubleshooting(false);
    }
  };

  const handleApplyFix = async () => {
    if (!pendingFix) return;
    setApplyingFix(true);
    try {
      const response: any = await permissionsManagerApi.applyFix(serverId, {
        fix: pendingFix,
        context: troubleshootContext,
      });
      queryClient.setQueryData(['servers', serverId, 'permissions', 'tree'], response.tree);
      toast.success('Soluzione IA applicata su Discord');
      setPendingFix(null);
      refetchPermissionTree();
      refetchStructure();
    } catch (error: any) {
      toast.error(error?.message || 'Errore applicazione fix');
    } finally {
      setApplyingFix(false);
    }
  };

  const handleMoveChannel = async (channelId: string, fromCategoryId: string, toCategoryId: string) => {
    if (fromCategoryId === toCategoryId) return;

    setMovingChannelId(channelId);
    try {
      const response: any = await permissionsManagerApi.moveChannel(serverId, channelId, {
        categoryId: toCategoryId === 'uncategorized' ? null : toCategoryId,
      });

      queryClient.setQueryData(['servers', serverId, 'permissions', 'tree'], response.tree);

      const movedChannel = (response.tree as any)?.categories
        ?.flatMap((cat: any) => cat.channels.map((ch: any) => ({ ...ch, categoryName: cat.name, categoryId: cat.id })))
        ?.find((ch: any) => ch.id === channelId);

      if (movedChannel && selectedChannel?.id === channelId) {
        setSelectedChannel(movedChannel);
        setSelectedCategoryName(movedChannel.categoryName);
      }

      toast.success(`Canale spostato in "${movedChannel?.categoryName || 'nuova categoria'}"`);
      refetchStructure();
    } catch (error: any) {
      toast.error(error?.message || 'Errore spostamento canale');
    } finally {
      setMovingChannelId(null);
    }
  };

  const summary = useMemo(() => {
    return {
      roles: structure?.roles?.length ?? 0,
      categories: structure?.categories?.length ?? 0,
      channels: (structure?.categories || []).reduce((acc: number, category: any) => acc + (category.channels?.length || 0), 0),
      changes:
        (plan?.summary?.rolesToCreate || 0) +
        (plan?.summary?.rolesToDelete || 0) +
        (plan?.summary?.categoriesToCreate || 0) +
        (plan?.summary?.categoriesToDelete || 0) +
        (plan?.summary?.channelsToCreate || 0) +
        (plan?.summary?.channelsToDelete || 0),
    };
  }, [structure, plan]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary/20 rounded-xl">
            <Bot className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-bold text-xl">AI Server Architect</h3>
            <p className="text-sm text-muted-foreground">
              {t('architect.subtitle')}
            </p>
          </div>
        </div>
        <Switch checked={isEnabled} disabled={togglingPlugin} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" /> {t('architect.chatTitle')}
            </CardTitle>
            <CardDescription>
              {t('architect.chatDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              placeholder={t('architect.goalsPlaceholder')}
              className="min-h-[80px]"
            />

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">{t('architect.destructiveTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('architect.destructiveDesc')}</p>
              </div>
              <Switch checked={allowDestructive} onCheckedChange={setAllowDestructive} />
            </div>

            <div className="rounded-md border p-3 max-h-[320px] overflow-auto space-y-3">
              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('architect.emptyChat')}</p>
              )}
              {messages.map((message, index) => (
                <div key={index} className={`p-3 rounded-md ${message.role === 'assistant' ? 'bg-primary/10' : 'bg-white/5'}`}>
                  <p className="text-xs mb-1 font-semibold">{message.role === 'assistant' ? t('architect.ai') : t('architect.you')}</p>
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('architect.inputPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend();
                }}
              />
              <Button onClick={handleSend} disabled={chatting || !isEnabled}>
                {chatting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" /> {t('architect.liveTitle')}
            </CardTitle>
            <CardDescription>
              {t('architect.liveDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded border p-2">
                <p className="text-xs text-muted-foreground">{t('architect.roles')}</p>
                <p className="text-xl font-semibold">{summary.roles}</p>
              </div>
              <div className="rounded border p-2">
                <p className="text-xs text-muted-foreground">{t('architect.categories')}</p>
                <p className="text-xl font-semibold">{summary.categories}</p>
              </div>
              <div className="rounded border p-2">
                <p className="text-xs text-muted-foreground">{t('architect.channels')}</p>
                <p className="text-xl font-semibold">{summary.channels}</p>
              </div>
              <div className="rounded border p-2">
                <p className="text-xs text-muted-foreground">{t('architect.plannedChanges')}</p>
                <p className="text-xl font-semibold">{summary.changes}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => refetchStructure()} disabled={loadingStructure}>
                {loadingStructure ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}{t('architect.refreshLive')}
              </Button>
              <Button variant="outline" onClick={() => handleApplyStream(true)} disabled={streamingApply || !isEnabled || !blueprint}>
                {streamingApply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}{t('architect.simulate')}
              </Button>
              <Button onClick={() => handleApplyStream(false)} disabled={streamingApply || !isEnabled || !blueprint}>
                {streamingApply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}{t('architect.applyDiscord')}
              </Button>
            </div>

            <div className="rounded-md border p-3 max-h-[220px] overflow-auto space-y-2">
              <p className="text-sm font-semibold">{t('architect.liveApplyLog')}</p>
              {liveEvents.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('architect.noLiveEvents')}</p>
              ) : (
                liveEvents.map((event, index) => (
                  <p key={`${event.type}-${index}`} className="text-xs text-muted-foreground">
                    [{event.type}] {event.message}
                  </p>
                ))
              )}
            </div>

            {plan?.summary && (
              <div className="rounded-md border p-3 space-y-2">
                <p className="text-sm font-semibold">{t('architect.planTitle')}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">{t('architect.roles')} +{plan.summary.rolesToCreate} / -{plan.summary.rolesToDelete}</Badge>
                  <Badge variant="secondary">{t('architect.categories')} +{plan.summary.categoriesToCreate} / -{plan.summary.categoriesToDelete}</Badge>
                  <Badge variant="secondary">{t('architect.channels')} +{plan.summary.channelsToCreate} / -{plan.summary.channelsToDelete}</Badge>
                  <Badge variant="secondary">{t('architect.overwrites')} {plan.summary.overwritesToApply}</Badge>
                </div>
              </div>
            )}

            {lastApplyResult?.summary && (
              <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3 text-sm">
                <p className="font-semibold mb-1">
                  {lastApplyResult?.dryRun ? t('architect.resultSimulation') : t('architect.resultApplication')}
                </p>
                <p className="text-muted-foreground">
                  {t('architect.summary')
                    .replace('{{rolesCreated}}', String(lastApplyResult.summary.rolesCreated))
                    .replace('{{rolesUpdated}}', String(lastApplyResult.summary.rolesUpdated))
                    .replace('{{rolesDeleted}}', String(lastApplyResult.summary.rolesDeleted))
                    .replace('{{categoriesCreated}}', String(lastApplyResult.summary.categoriesCreated))
                    .replace('{{categoriesDeleted}}', String(lastApplyResult.summary.categoriesDeleted))
                    .replace('{{channelsCreated}}', String(lastApplyResult.summary.channelsCreated))
                    .replace('{{channelsDeleted}}', String(lastApplyResult.summary.channelsDeleted))}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" /> Albero permessi & Troubleshooting IA
          </CardTitle>
          <CardDescription>
            Equivalente al TreeView UMG: esplora categorie/canali, modifica overwrite manualmente o chiedi all&apos;IA di risolvere problemi vocali/testo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Struttura server</p>
                <Button variant="ghost" size="sm" onClick={() => refetchPermissionTree()} disabled={!isEnabled}>
                  <RefreshCcw className="w-4 h-4" />
                </Button>
              </div>
              <PermissionTreeView
                categories={(permissionTree as any)?.categories || []}
                selectedChannelId={selectedChannel?.id || null}
                onSelectChannel={handleSelectChannel}
                onMoveChannel={isEnabled ? handleMoveChannel : undefined}
                movingChannelId={movingChannelId}
              />
            </div>

            <div>
              {selectedChannel ? (
                <PermissionDetailPanel
                  channelName={selectedChannel.name}
                  channelType={selectedChannel.type}
                  categoryName={selectedCategoryName}
                  roles={treeRoles}
                  permissions={channelPermissions}
                  selectedRoleId={selectedRoleId}
                  onRoleChange={setSelectedRoleId}
                  values={permissionDraft}
                  onChange={(permission, value) =>
                    setPermissionDraft((prev) => ({ ...prev, [permission]: value }))
                  }
                  onSave={handleSavePermissions}
                  saving={savingPermissions}
                />
              ) : (
                <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                  Seleziona un canale dall&apos;albero per modificare i permessi.
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <p className="text-sm font-medium">AI Troubleshooting</p>
              </div>
              <Textarea
                value={troubleshootInput}
                onChange={(e) => setTroubleshootInput(e.target.value)}
                placeholder='Es: Domenico non riesce a parlare nel canale "Supporto Tecnico"'
                className="min-h-[100px]"
                disabled={!isEnabled}
              />
              <Button onClick={handleTroubleshoot} disabled={!isEnabled || troubleshooting} className="w-full">
                {troubleshooting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Analizza con IA
              </Button>

              {pendingFix && (
                <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3 text-sm">
                  <p className="font-medium">Diagnosi IA</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{pendingFix.explanation_it}</p>
                  {pendingFix.action === 'MODIFY_PERMISSIONS' && (
                    <Button onClick={handleApplyFix} disabled={applyingFix} className="w-full">
                      {applyingFix ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Applica modifiche su Discord
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
